// FinishSetupPill — a small, muted "Finish setting up your account" alert
// that sits just above the "Hello, <name>" greeting on the Creator Dashboard.
//
// Jun 2026 (Randy) — Onboarding nudge for brand-new creators who can't yet
// run answers because they have NOT connected a working brain. The pill is
// shown until GEMINI specifically is set up — either a Gemini BYOK key OR a
// funded Gemini voucher. Claude / OpenAI alone do NOT count (Gemini is the
// free-tier-friendly default that makes a silo actually work cheaply).
//
// Tone: small + dull/muted red. NOT a loud banner. Tapping it opens a warm,
// plain-English explainer + a "Start here" button that deep-links to
// Account & Billing's key-setup area.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Warning, X, ArrowRight, Key, Wallet } from "@phosphor-icons/react";
import { api, getUser } from "@/lib/api";
import { useTrial, money } from "@/lib/trial";
import UniqueWaysModal from "@/components/UniqueWaysModal";

export function FinishSetupModal({ onClose, onStart, trial: trialVariant = false }) {
  const [showWays, setShowWays] = useState(false);
  const { trial } = useTrial();
  const grant = trial?.credit?.grant_cents;
  const provider = trial?.credit?.provider || "Gemini";
  const days = trial?.trial_days || 14;
  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="finish-setup-modal"
    >
      <div className="absolute inset-0 bg-[#1C1B1A]/45 backdrop-blur-sm" />
      <div
        className="relative z-10 bg-white rounded-2xl max-w-lg w-full max-h-[90dvh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-6 sm:p-7 pb-3 shrink-0">
          <h3 className="font-serif-display text-2xl text-[#1C1B1A] leading-tight">
            {trialVariant ? <>Your {days} days have started.</> : <>Welcome to My Silo.</>}
          </h3>
          <button
            onClick={onClose}
            data-testid="finish-setup-modal-close"
            className="text-[#6B6A68] hover:text-[#1C1B1A] p-1 shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3 text-sm text-[#3A3A38] leading-relaxed px-6 sm:px-7 overflow-y-auto flex-1">
          {/* Oct 2026 (Randy, fan-first pass) — trial starters used to skip this
              explainer entirely and land in the dashboard with a dollar of
              credit and no idea the AI needs funding at all. They get the same
              lesson as a paid creator, with the one difference that matters:
              they don't have to do anything about it today. */}
          {trialVariant && (
            <div className="rounded-lg border border-[#4A5D4E]/40 bg-[#E8EBE5]/60 p-3.5" data-testid="finish-setup-trial-note">
              <div className="flex items-center gap-2 text-[#2F6B3A] font-medium text-[13px] mb-1">
                <Wallet size={15} weight="duotone" /> You already have fuel in the tank
              </div>
              <p className="text-[13px] text-[#5C5B59]">
                Your trial opens with <strong>{money(grant)} of {provider} credit at our cost</strong> —
                nothing to set up, nothing to pay. Because My Silo structures your corpus so tightly,
                that dollar goes a long way on the efficient mini models: hundreds of real questions,
                not a handful. It's there so you can watch the thing work before you fund anything.
                It expires with the trial, so here's what you'll want to understand before day {days + 1}.
              </p>
            </div>
          )}
          <p>
            Think of the AI wave as digital real estate; it's time to claim a space that represents
            you. While public AI models are powerful, they generate ambiguous noise. My Silo counters
            this by offering absolute control. By building a precise digital twin, you ensure the
            system adopts your voice and adheres strictly to your personal data.
          </p>

          <div className="text-center py-1">
            <button
              type="button"
              onClick={() => setShowWays(true)}
              data-testid="unique-ways-link"
              className="text-[#4A5D4E] hover:text-[#3A4A3E] underline underline-offset-2 font-medium text-[15px] transition-colors"
            >
              – Unique ways to utilize My Silo –
            </button>
          </div>

          <p>
            Because your silo leverages established models like ChatGPT, Gemini, and Claude — and
            we're utilizing them without their native restrictions and outside of their home turf —
            they don't just give away their knowledge base for nothing. Fortunately, they offer their
            services to anyone for pennies on the dollar. And with a tool such as My Silo, which has
            gone the extra mile to design extreme efficiency into the way your data is structured, we
            can have our cake and eat it too — at a price point that is extremely satisfying.
          </p>
          <p>My Silo offers two ways to tap into these powerhouse LLMs:</p>

          <div className="rounded-lg border border-[#E5BBA8] bg-[#FDF1ED]/50 p-3.5">
            <div className="flex items-center gap-2 text-[#9A4A38] font-medium text-[13px] mb-1">
              <Wallet size={15} weight="duotone" /> Option A · Stay in-house (voucher top-up)
            </div>
            <p className="text-[13px] text-[#5C5B59]">
              Use My Silo's website keys to open access — simply top up your voucher balance and we
              handle the connection for you. It just costs a fraction more per question (about 3¢ vs 1¢).
            </p>
          </div>

          <div className="rounded-lg border border-[#4A5D4E]/40 bg-[#E8EBE5]/50 p-3.5">
            <div className="flex items-center gap-2 text-[#2F6B3A] font-medium text-[13px] mb-1">
              <Key size={15} weight="duotone" /> Option B · Bypass the middleman (save up to 3×)
            </div>
            <p className="text-[13px] text-[#5C5B59]">
              A brief one-minute setup per LLM hands you the master keys. Something that might cost
              three pennies, we only cost one. My Silo becomes a highly efficient, markup-free conduit
              — and we show you exactly how, with direct links.
            </p>
          </div>

          <p>
            Either way, most of your everyday questions will likely be <strong>free or just
            pennies</strong>. Making the world's most famous AI models available to you at the lowest
            possible cost was our highest priority — the choice is yours.
          </p>
        </div>

        <div className="shrink-0 border-t hairline px-6 sm:px-7 pt-4 pb-[calc(1rem_+_env(safe-area-inset-bottom))]">
          <button
            onClick={onStart}
            data-testid="finish-setup-start-btn"
            className="w-full px-6 py-3 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85E4A] transition-colors flex items-center justify-center gap-2 shadow-sm text-sm"
          >
            {trialVariant ? "Show me the key setup" : "Start here"} <ArrowRight size={15} weight="bold" />
          </button>
        </div>
      </div>
      {showWays && <UniqueWaysModal onClose={() => setShowWays(false)} />}
    </div>
  );
}

export default function FinishSetupPill() {
  const nav = useNavigate();
  const { trial } = useTrial();
  const [geminiReady, setGeminiReady] = useState(true); // assume ready until known → no flash
  const [loaded, setLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const user = getUser();
    // Fans never reach the dashboard; guard anyway so the pill is creator-only.
    if (user?.role === "fan") { setLoaded(true); return; }
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/me/voucher-summary");
        if (cancel) return;
        const byok = r.data?.byok_status || {};
        const voucher = r.data?.provider_totals_cents || {};
        // Gemini specifically — a key OR a funded voucher clears the nudge.
        const ready = !!byok.gemini || (voucher.gemini || 0) > 0;
        setGeminiReady(ready);
      } catch {
        // If we can't tell, don't nag.
        setGeminiReady(true);
      } finally {
        if (!cancel) setLoaded(true);
      }
    })();
    return () => { cancel = true; };
  }, []);

  if (!loaded || geminiReady) return null;

  // Oct 2026 (TRIAL_PLAN.md §8.11) — a live trial grants Gemini credit, so this
  // pill is already quiet while the dollar lasts. When it runs out the nudge
  // comes back, and it should say WHY rather than "finish setting up".
  const c = trial?.credit || {};
  const trialCreditSpent = !!trial?.live && !!c.granted_at && !c.voided_at
    && Number(c.remaining_cents || 0) === 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        data-testid="finish-setup-pill"
        className="inline-flex items-center gap-1.5 mb-4 px-3 py-1.5 rounded-full border border-[#D9B3A8] bg-[#F6E7E1] text-[#8A4533] text-xs font-medium hover:bg-[#F0DAD2] transition-colors"
      >
        <Warning size={13} weight="duotone" />
        {trialCreditSpent
          ? "Your trial credit is used up — top up or add your own Gemini key"
          : "Finish setting up your account"}
      </button>
      {showModal && (
        <FinishSetupModal
          onClose={() => setShowModal(false)}
          onStart={() => { setShowModal(false); nav("/dashboard/account-settings?focus=byok"); }}
        />
      )}
    </>
  );
}
